import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type CheckInStats, type CheckInGuest } from '../api/client';

export function useCheckInStats(eventId: string | undefined) {
  return useQuery<CheckInStats>({
    queryKey: ['checkin-stats', eventId],
    queryFn: () => api.checkin.stats(eventId!),
    enabled: !!eventId,
  });
}

export function useGenerateQrCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => api.checkin.generateQr(eventId),
    onSuccess: (_result, eventId) => {
      queryClient.invalidateQueries({ queryKey: ['guests', eventId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', eventId] });
    },
  });
}

export function useQrCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { eventId: string; qrCode: string }) =>
      api.checkin.qrCheckIn(data.eventId, data.qrCode),
    onSuccess: (_result: { guest: CheckInGuest }, variables) => {
      queryClient.invalidateQueries({ queryKey: ['checkin-stats', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['guests', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', variables.eventId] });
    },
  });
}

export function useManualCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { eventId: string; guestId: string }) =>
      api.checkin.manualCheckIn(data.eventId, data.guestId),
    onSuccess: (_result: { guest: CheckInGuest }, variables) => {
      queryClient.invalidateQueries({ queryKey: ['checkin-stats', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['guests', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', variables.eventId] });
    },
  });
}

export function useUndoCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { eventId: string; guestId: string }) =>
      api.checkin.undoCheckIn(data.eventId, data.guestId),
    onSuccess: (_result: { guest: CheckInGuest }, variables) => {
      queryClient.invalidateQueries({ queryKey: ['checkin-stats', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['guests', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', variables.eventId] });
    },
  });
}
